import React, {useCallback, useContext, useState} from 'react';
import {
    Box,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {Cancel, CheckCircle, Edit} from '@mui/icons-material';
import ZeiterfassungDashboardContext from '../../../../../../Contexts/ZeiterfassungDashboardContext/ZeiterfassungDashboardContext';
import {ArbeitszeitService} from '../../../../../../services/openapi/services/ArbeitszeitService';
import APIWrapperContext from '../../../../../../services/APIWrapperContext';
import EditArbeitszeiteintrag from './EditArbeitszeiteintrag';
import {IWorktimeDetailsProps} from './types';

const WorktimeDetails: React.FC<IWorktimeDetailsProps> = ({details, isAdmin, bauvorhabenList}) => {
    const [editModeMap, setEditModeMap] = useState<{[key: number]: boolean}>({});
    const [deleteConfirmMap, setDeleteConfirmMap] = useState<{[key: number]: boolean}>({});
    const {call_backend_api} = useContext(APIWrapperContext);
    const {handle_Upadate_Arbeitszeiten_alle_MA} = useContext(ZeiterfassungDashboardContext);

    const updateArbeitszeiteintrag = useCallback(
        (arbeitszeiteintrag: any) => {
            call_backend_api(
                ArbeitszeitService.updateWorktimeAdminArbeitzeitArbeitszeiteintragAdminPut(arbeitszeiteintrag),
                (response: any) => {
                    setEditModeMap((prev) => ({...prev, [arbeitszeiteintrag.id]: false}));
                    if (handle_Upadate_Arbeitszeiten_alle_MA) {
                        handle_Upadate_Arbeitszeiten_alle_MA();
                    }
                    console.log('Successfully updated worktime entry:', response);
                },
                (error: any) => {
                    console.error('Error updating worktime entry:', error);
                },
                undefined,
                true
            );
        },
        [call_backend_api, handle_Upadate_Arbeitszeiten_alle_MA]
    );

    const handleEditClick = useCallback((id: number) => {
        setEditModeMap((prev) => ({...prev, [id]: true}));
    }, []);

    const handleCancelEdit = useCallback((id: number) => {
        setEditModeMap((prev) => ({...prev, [id]: false}));
    }, []);

    const deleteArbeitszeiteintrag = useCallback(
        (id: number) => {
            call_backend_api(
                ArbeitszeitService.deleteWorktimeEntryAdminArbeitzeitArbeitszeiteintragIdDelete(id),
                (response: any) => {
                    setDeleteConfirmMap((prev) => ({...prev, [id]: false}));
                    if (handle_Upadate_Arbeitszeiten_alle_MA) {
                        handle_Upadate_Arbeitszeiten_alle_MA();
                    }
                    console.log('Successfully deleted worktime entry:', response);
                },
                (error: any) => {
                    console.error('Error deleting worktime entry:', error);

                    if (error?.status === 403) {
                        alert('Sie haben keine Berechtigung, Arbeitszeiten zu löschen. Nur Administratoren können diese Aktion durchführen.');
                    } else if (error?.status === 404) {
                        alert('Der Arbeitszeiteintrag konnte nicht gefunden werden.');
                    } else if (error?.status === 401) {
                        alert('Sie sind nicht angemeldet oder Ihre Session ist abgelaufen.');
                    } else {
                        alert('Fehler beim Löschen des Arbeitszeiteintrag. Bitte versuchen Sie es erneut.');
                    }

                    setDeleteConfirmMap((prev) => ({...prev, [id]: false}));
                },
                undefined,
                true
            );
        },
        [call_backend_api, handle_Upadate_Arbeitszeiten_alle_MA]
    );

    const handleCancelDelete = useCallback((id: number) => {
        setDeleteConfirmMap((prev) => ({...prev, [id]: false}));
    }, []);

    const handleConfirmDelete = useCallback(
        (id: number) => {
            deleteArbeitszeiteintrag(id);
        },
        [deleteArbeitszeiteintrag]
    );

    return (
        <Box sx={{margin: 1, p: 1, borderRadius: '8px', backgroundColor: 'rgba(0, 0, 0, 0.01)'}}>
            <Typography variant='subtitle1' fontWeight={600} sx={{mb: 2}} component='div'>
                Details
            </Typography>
            <Table size='small' aria-label='purchases'>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <Typography variant='body2' fontWeight={600}>
                                Bauvorhaben
                            </Typography>
                        </TableCell>
                        <TableCell align='right'>
                            <Typography variant='body2' fontWeight={600}>
                                Beginn
                            </Typography>
                        </TableCell>
                        <TableCell align='right'>
                            <Typography variant='body2' fontWeight={600}>
                                Ende
                            </Typography>
                        </TableCell>
                        <TableCell align='right'>
                            <Typography variant='body2' fontWeight={600}>
                                Dauer
                            </Typography>
                        </TableCell>
                        <TableCell align='right'>
                            <Typography variant='body2' fontWeight={600}>
                                Pause
                            </Typography>
                        </TableCell>
                        <TableCell align='right'>
                            <Typography variant='body2' fontWeight={600}>
                                Tätigkeit
                            </Typography>
                        </TableCell>
                        <TableCell align='right'>
                            <Typography variant='body2' fontWeight={600}>
                                Kurzinfo
                            </Typography>
                        </TableCell>
                        <TableCell align='right'>
                            <Typography variant='body2' fontWeight={600}>
                                Aktionen
                            </Typography>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {details.map((detailRow) =>
                        editModeMap[detailRow.id] ? (
                            <EditArbeitszeiteintrag
                                key={`edit-row-${detailRow.id}`}
                                arbeitszeiteintrag={detailRow}
                                updateArbeitszeiteintrag={updateArbeitszeiteintrag}
                                onCancel={() => handleCancelEdit(detailRow.id)}
                                onDelete={deleteArbeitszeiteintrag}
                                bauvorhabenList={bauvorhabenList}
                            />
                        ) : (
                            <TableRow
                                key={`view-row-${detailRow.id}`}
                                sx={{
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.03)',
                                    },
                                    transition: 'background-color 0.2s',
                                }}>
                                <TableCell component='th' scope='row'>
                                    <Typography variant='body2'>{detailRow.BVH}</Typography>
                                </TableCell>
                                <TableCell align='right'>
                                    <Typography variant='body2'>{detailRow.BEGINN}</Typography>
                                </TableCell>
                                <TableCell align='right'>
                                    <Typography variant='body2'>{detailRow.ENDE}</Typography>
                                </TableCell>
                                <TableCell align='right'>
                                    <Typography variant='body2' fontWeight={500}>
                                        {detailRow.DAUER.toFixed(2)}
                                    </Typography>
                                </TableCell>
                                <TableCell align='right'>
                                    <Typography variant='body2'>{detailRow.PAUSE.toFixed(2)}</Typography>
                                </TableCell>
                                <TableCell align='right'>
                                    <Typography variant='body2'>{detailRow.TAETIGKEIT || '-'}</Typography>
                                </TableCell>
                                <TableCell align='right'>
                                    <Typography variant='body2'>{detailRow.KURZINFO || '-'}</Typography>
                                </TableCell>
                                <TableCell align='right'>
                                    {isAdmin && (
                                        <Box sx={{display: 'flex', gap: 0.5}}>
                                            {deleteConfirmMap[detailRow.id] ? (
                                                <>
                                                    <IconButton
                                                        size='small'
                                                        onClick={() => handleConfirmDelete(detailRow.id)}
                                                        title='Löschen bestätigen'
                                                        sx={{
                                                            color: 'error.main',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(211, 47, 47, 0.08)',
                                                            },
                                                        }}>
                                                        <CheckCircle sx={{fontSize: 18}} />
                                                    </IconButton>
                                                    <IconButton
                                                        size='small'
                                                        onClick={() => handleCancelDelete(detailRow.id)}
                                                        title='Löschen abbrechen'
                                                        sx={{
                                                            color: 'text.secondary',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                                            },
                                                        }}>
                                                        <Cancel sx={{fontSize: 18}} />
                                                    </IconButton>
                                                </>
                                            ) : (
                                                <IconButton
                                                    size='small'
                                                    onClick={() => handleEditClick(detailRow.id)}
                                                    title='Eintrag bearbeiten'
                                                    sx={{
                                                        color: 'primary.main',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                                        },
                                                    }}>
                                                    <Edit sx={{fontSize: 18}} />
                                                </IconButton>
                                            )}
                                        </Box>
                                    )}
                                </TableCell>
                            </TableRow>
                        )
                    )}
                </TableBody>
            </Table>
        </Box>
    );
};

export default React.memo(WorktimeDetails);
